import React, { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { keyType, Typography } from '@agilepoint/wat-react-ux';
import { TextAreaField } from '@agilepoint/wat-react-ux-form-fields';
import { ElasticTextBoxField, emailRegExp, GridLayout, websiteRegExp } from 'common';
import { SectionLabel, FieldWrapper, BasicSettingsWrapper, StyledDropdownField, TextAreaWrapper, TextCounter, StyledRadioGroupField } from '../styled-components';
import { useValidateHelperFunctions } from 'lib/hooks/validate-helper-functions';

export const useLocaleValues = () => [
    { key: 'default', text: 'Default Language', value: 'default' },
    { key: 'en-US', text: 'English', value: 'en-US' },
    { key: 'ja-JP', text: 'Japanese', value: 'ja-JP' },
    { key: 'fr', text: 'French', value: 'fr' },
    { key: 'de', text: 'German', value: 'de' },
    { key: 'nl', text: 'Dutch', value: 'nl' },
    { key: 'es', text: 'Spanish', value: 'es' },
    { key: 'zh-CN', text: 'Chinese-Traditional', value: 'zh-CN' },
    { key: 'zh-TW', text: 'Chinese-Simplified', value: 'zh-TW' },
    { key: 'tr-TR', text: 'Turkish', value: 'tr-TR' },
    { key: 'ca', text: 'Catalan', value: 'ca' }
];

export const BasicSettingsForm = React.memo(() => {
    const translate = useTranslate();
    const localizedLocaleItems = useLocaleValues();
    const { watch, setValue } = useFormContext();
    const [textLength, setTextLength] = useState(watch('mobileAppDescription')?.length ?? 0);
    const { validateTextLength } = useValidateHelperFunctions();

    const onChange = useCallback((ev) => setTextLength(ev.target.value.length), []);

    const eulaRadioOptions = [
        {
            name: 'Standard EULA',
            value: 'standardEULA',
            title: 'Standard EULA',
            key: 'standardEULA'
        },
        {
            name: 'Custom EULA',
            value: 'customEULA',
            title: 'Custom EULA',
            key: 'customEULA'
        }
    ];

    const [isCustomEula, setIsCustomEula] = useState(watch('eulaOptions') !== 'standardEULA');

    useEffect(() => {
        const subscription = watch((value) => {
            setIsCustomEula(value.eulaOptions === 'customEULA');
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    const onValueChange = useCallback((selectedKey: keyType) => {
        if (selectedKey === 'customEULA') {
            setIsCustomEula(true);
        }
        else {
            setIsCustomEula(false);
            setValue('eulaText', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
            setValue('eulaLink', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        }
        // Note: temp code needs to be fixed later
        document.getElementsByClassName('core-app-content-area')[0].scrollTo(0, 0);
    }, [setValue]);

    return (
        <BasicSettingsWrapper orientation='vertical' gap="20" width="670px" height="100%">
            <GridLayout $gridTemplateColumns="repeat(2, 1fr)" $width="100%" $gridGap="0 20px">
                <FieldWrapper>
                    <ElasticTextBoxField
                        width='100%'
                        title={translate('modules.createNewApp.steps.basicSettings.mobileAppName')}
                        label={translate('modules.createNewApp.steps.basicSettings.mobileAppName')}
                        name="mobileAppName"
                        validationRules={{ required: translate('modules.createNewApp.steps.basicSettings.validationMessages.requiredMobileAppName'), validate: validateTextLength }}
                    />
                </FieldWrapper>
                <ElasticTextBoxField
                    width='100%'
                    title={translate('modules.createNewApp.steps.basicSettings.mobileAppVersion')}
                    label={translate('modules.createNewApp.steps.basicSettings.mobileAppVersion')}
                    name="mobileAppVersion"
                    validationRules={{ required: translate('modules.createNewApp.steps.basicSettings.validationMessages.requiredMobileAppVersion') }} />
            </GridLayout>
            <StyledDropdownField
                name="defaultLocale"
                gap="4"
                items={localizedLocaleItems}
                validationRules={{ required: translate('modules.createNewApp.steps.basicSettings.validationMessages.requiredDefaultLocale') }}
                title={translate('modules.createNewApp.steps.basicSettings.defaultLocale')}
                label={translate('modules.createNewApp.steps.basicSettings.defaultLocale')} />
            <TextAreaWrapper>
                <TextAreaField
                    name="mobileAppDescription"
                    gap="4"
                    label={translate('modules.createNewApp.steps.basicSettings.mobileAppDescription')}
                    title={translate('modules.createNewApp.steps.basicSettings.mobileAppDescription')}
                    maxLength={2000}
                    onChange={onChange} />
                <TextCounter>{`${textLength}/2000`}</TextCounter>
            </TextAreaWrapper>
            <SectionLabel title={translate('modules.createNewApp.steps.basicSettings.company')}>
                <Typography.H6><Localize translate='modules.createNewApp.steps.basicSettings.company' /></Typography.H6>
            </SectionLabel>
            <GridLayout $gridTemplateColumns="repeat(2, 1fr)" $width="100%" $gridGap="20px 20px">
                <ElasticTextBoxField
                    $width='100%'
                    title={translate('modules.createNewApp.steps.basicSettings.companyName')}
                    label={translate('modules.createNewApp.steps.basicSettings.companyName')}
                    name="companyName"
                    validationRules={{ required: translate('modules.createNewApp.steps.basicSettings.validationMessages.requiredCompanyName') }} />
                <ElasticTextBoxField
                    title={translate('modules.createNewApp.steps.basicSettings.companyWebsite')}
                    label={translate('modules.createNewApp.steps.basicSettings.companyWebsite')}
                    name="companyWebsite"
                    $width='100%'
                    validationRules={{
                        required: translate('modules.createNewApp.steps.basicSettings.validationMessages.requiredCompanyWebsite'),
                        pattern: { value: websiteRegExp, message: translate('modules.createNewApp.steps.basicSettings.validationMessages.validSiteUriText') }
                    }} />
                <ElasticTextBoxField
                    type="email"
                    $width='100%'
                    title={translate('modules.createNewApp.steps.basicSettings.companySupportEmail')}
                    label={translate('modules.createNewApp.steps.basicSettings.companySupportEmail')}
                    name="companySupportEmail"
                    validationRules={{
                        required: translate('modules.createNewApp.steps.basicSettings.validationMessages.requiredCompanySupportEmail'),
                        pattern: { value: emailRegExp, message: translate('modules.createNewApp.steps.basicSettings.validationMessages.validEmailIdText') }
                    }} />
                <ElasticTextBoxField
                    type="email"
                    $width='100%'
                    title={translate('modules.createNewApp.steps.basicSettings.companyInfoEmail')}
                    label={translate('modules.createNewApp.steps.basicSettings.companyInfoEmail')}
                    name="companyInfoEmail"
                    validationRules={{
                        required: translate('modules.createNewApp.steps.basicSettings.validationMessages.requiredCompanyInfoEmail'),
                        pattern: { value: emailRegExp, message: translate('modules.createNewApp.steps.basicSettings.validationMessages.validEmailIdText') }
                    }} />
            </GridLayout>
            <ElasticTextBoxField
                title={translate('modules.createNewApp.steps.basicSettings.copyrightInfo')}
                label={translate('modules.createNewApp.steps.basicSettings.copyrightInfo')}
                name="copyrightInfo"
                width='100%' />
            <SectionLabel title={translate('modules.createNewApp.steps.basicSettings.eulaLabel')}>
                <Typography.H6><Localize translate='modules.createNewApp.steps.basicSettings.eulaLabel' /></Typography.H6>
            </SectionLabel>
            <StyledRadioGroupField groupName="eula-options"
                options={eulaRadioOptions}
                onValueChange={onValueChange}
                name="eulaOptions" />
            {isCustomEula ?
                <>
                    <ElasticTextBoxField
                        title="EULA Text"
                        label="EULA Text"
                        name="eulaText"
                        width='100%'
                    />
                    <ElasticTextBoxField
                        title={translate('modules.createNewApp.steps.basicSettings.eulaLink')}
                        label={translate('modules.createNewApp.steps.basicSettings.eulaLink')}
                        name="eulaLink"
                        width='100%'
                        validationRules={{
                            pattern: { value: websiteRegExp, message: translate('modules.createNewApp.steps.basicSettings.validationMessages.validSiteUriText') }
                        }} /></> : <></>}

        </BasicSettingsWrapper>
    );
});
